import { classToPlain, Exclude, Expose, plainToClass } from "class-transformer";
import * as t from "io-ts";
import "reflect-metadata";
import { v4 as uuid } from "uuid";
import { validateJSON } from "../web/validation";
import type { SystemAlertEntity, SystemAlertType } from "./alert";
import { SystemAlertBaseValidator, systemAlertType } from "./alert";
import { SiteEntity } from "./site";
import { DatabaseCode, toDatabaseCode } from "./database-code";

export const SiteAlertValidator = t.intersection([
  SystemAlertBaseValidator,
  t.type({
    id: t.string,
    siteId: t.string,
    regionId: t.string,
    alertType: t.string,
    alertDttm: t.number,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    comment: t.string,
    createdById: t.string,
    triggerObjectId: t.string,
    triggerObjectType: t.string,
    database: t.string,
  }),
]);

export const SiteAlertArrayValidator = t.array(SiteAlertValidator);

export type SiteAlert = t.TypeOf<typeof SiteAlertValidator>;
export type SiteAlertArray = t.TypeOf<typeof SiteAlertArrayValidator>;

export interface SiteAlertType extends DatabaseCode {}

const highH2S: SiteAlertType = {
  code: 'HIGH_H2S',
  displayValue: {
    id: 'ots.ui.entity.sitealert.type.highh2s',
    defaultMessage: 'High H2S',
  },
};

export const siteAlertType = {
  highH2S,
};

export const siteAlertTypes = Object.values(siteAlertType)

export const toSiteAlertType = (code: string): SiteAlertType => toDatabaseCode(code, siteAlertTypes)

/**
 * A site alert.
 */
@Exclude()
export class SiteAlertEntity implements SystemAlertEntity {
  @Expose() id!: string;
  @Expose() siteId!: string;
  @Expose() regionId!: string;
  @Expose({ name: 'alertType' }) private _alertType!: string;
  @Expose() private alertDttm!: number;
  @Expose() comment?: string;
  @Expose() createdById?: string;
  @Expose() triggerObjectId?: string;
  @Expose() triggerObjectType?: string;
  @Expose() database?: string;
  @Expose() private createDttm!: number;
  @Expose() private lastModifiedDttm!: number;

  // for system alert
  @Expose() private systemAlertDttm?: number;

  /**
   * Constructor for SiteAlertEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static builder(id: string = uuid()): SiteBuilderStep {
    return new SiteAlertBuilder(id);
  }

  /**
   * Create a SiteAlertEntity.
   *
   * @param builder the site alert builder
   */
  static create(builder: SiteAlertBuilder): SiteAlertEntity {
    const entity = new SiteAlertEntity();
    entity.id = builder.id;
    entity.siteId = builder.siteId;
    entity.regionId = builder.regionId;
    entity.alertType = builder.alertType;
    entity.alertDate = builder.alertDate;
    entity.comment = builder.comment;
    entity.createdById = builder.createdById;
    entity.triggerObjectId = builder.triggerObjectId;
    entity.triggerObjectType = builder.triggerObjectType;
    entity.createDate = builder.createDate;
    entity.lastModifiedDate = builder.lastModifiedDate;
    return entity;
  }

  /**
   * Create a SiteAlertEntity from a SiteAlert type value.
   *
   * @param siteAlert The site alert type value.
   */
  static from(siteAlert: SiteAlert): SiteAlertEntity {
    const entity: SiteAlertEntity = plainToClass(SiteAlertEntity, siteAlert, {
      excludeExtraneousValues: true,
    });
    return entity;
  }

  /**
   * Deserializes the SiteAlertEntity to a SiteAlert type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<SiteAlert> {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(SiteAlertValidator, object);
  }

  get alertType(): SiteAlertType {
    return toSiteAlertType(this._alertType);
  }

  set alertType(type: SiteAlertType) {
    this._alertType = type.code;
  }

  get alertDate(): Date {
    return new Date(this.alertDttm);
  }

  set alertDate(date: Date) {
    this.alertDttm = date.getTime();
  }

  get createDate(): Date {
    return new Date(this.createDttm);
  }

  set createDate(date: Date) {
    this.createDttm = date.getTime();
  }

  get lastModifiedDate(): Date {
    return new Date(this.lastModifiedDttm);
  }

  set lastModifiedDate(date: Date) {
    this.lastModifiedDttm = date.getTime();
  }

  set site(site: SiteEntity) {
    this.siteId = site.id;
  }

  getSystemAlertId = (): string => {
    return this.id;
  };

  getSystemAlertType = (): SystemAlertType => {
    return systemAlertType.siteAlert;
  };

  getSystemAlertDate = (): Date | undefined => {
    return this.systemAlertDttm ? new Date(this.systemAlertDttm) : undefined;
  };
}

/**
 * Site Alert Builder
 */

interface AlertDateBuilderStep {
  setAlertDate: (value: Date) => SiteAlertBuilder;
}

interface SiteBuilderStep {
  setSite: (value: string) => RegionBuilderStep;
}

interface RegionBuilderStep {
  setRegion: (value: string) => AlertTypeBuilderStep;
}

interface AlertTypeBuilderStep {
  setAlertType: (value: SiteAlertType) => SiteAlertBuilder;
}

export class SiteAlertBuilder
  implements
    AlertTypeBuilderStep,
    RegionBuilderStep,
    SiteBuilderStep,
    AlertDateBuilderStep
{
  id: string;

  siteId!: string;
  regionId!: string;
  alertType!: SiteAlertType;
  alertDate!: Date;

  comment?: string;
  createdById?: string;
  triggerObjectId?: string;
  triggerObjectType?: string;
  createDate = new Date();
  lastModifiedDate = new Date();

  constructor(id: string | undefined = uuid()) {
    this.id = id;
  }

  setComment(value: string | undefined): SiteAlertBuilder {
    this.comment = value;
    return this;
  }

  setCreatedById(value: string | undefined): SiteAlertBuilder {
    this.createdById = value;
    return this;
  }

  setTriggerObjectId(value: string | undefined): SiteAlertBuilder {
    this.triggerObjectId = value;
    return this;
  }

  setTriggerObjectType(value: string | undefined): SiteAlertBuilder {
    this.triggerObjectType = value;
    return this;
  }

  setCreateDate(value: Date): SiteAlertBuilder {
    this.createDate = value;
    return this;
  }

  setLastModifiedDate(value: Date): SiteAlertBuilder {
    this.lastModifiedDate = value;
    return this;
  }

  setSite(value: string): RegionBuilderStep {
    this.siteId = value;
    return this;
  }

  setRegion(value: string): AlertTypeBuilderStep {
    this.regionId = value;
    return this;
  }

  setAlertType(value: SiteAlertType): SiteAlertBuilder {
    this.alertType = value;
    return this;
  }

  setAlertDate(value: Date): SiteAlertBuilder {
    this.alertDate = value;
    return this;
  }

  build(): SiteAlertEntity {
    return SiteAlertEntity.create(this);
  }
}
